/**
 * Chart service: REST history loader + live WebSocket stream + client-side
 * aggregation of 1m candles into the user's selected interval.
 *
 * Server only ever pushes 1m candles. This module:
 *   - Fetches history at the requested interval via /chart/history
 *   - Subscribes to /ws/chart per symbol and aggregates incoming 1m candles
 *     into the current interval's running bar.
 */

import axios from '../../api/axiosApi'

const CHART_API = (window.config.chartUrl || '').replace(/\/+$/, '')
const CHART_WS  = (window.config.chartWS  || '').replace(/\/+$/, '')

/** Seconds per interval. 1M = 30d (approx; only used for bucket math, not for the API call). */
export const INTERVAL_SECONDS = {
  '1m':  60,
  '5m':  5 * 60,
  '15m': 15 * 60,
  '1h':  60 * 60,
  '4h':  4 * 60 * 60,
  '1d':  24 * 60 * 60,
  '1w':  7 * 24 * 60 * 60,
  '1M':  30 * 24 * 60 * 60,
}

/**
 * Maximum lookback window (seconds) per interval. The backend returns the
 * latest N bars within this window (ORDER BY bucket DESC LIMIT N, reversed),
 * so a wide window doesn't bloat the payload — it just lets us surface the
 * last-known session when the market is currently closed.
 */
export const INTERVAL_DEFAULT_LOOKBACK = {
  '1m':  30  * 24 * 60 * 60,           // 30d (also matches candles_1m retention)
  '5m':  90  * 24 * 60 * 60,
  '15m': 90  * 24 * 60 * 60,
  '1h':  365 * 24 * 60 * 60,
  '4h':  365 * 24 * 60 * 60,
  '1d':  5   * 365 * 24 * 60 * 60,
  '1w':  10  * 365 * 24 * 60 * 60,
  '1M':  30  * 365 * 24 * 60 * 60,
}

/** Bars to render on initial load — keep payload sensible. */
export const DEFAULT_BARS = 1000

/**
 * Fetch historical candles.
 * @returns Array<{time, open, high, low, close}> ready for Lightweight Charts (time in seconds).
 */
export async function fetchHistory(symbol, interval, fromMs, toMs, limit = DEFAULT_BARS) {
  const params = { symbol, interval, limit }
  if (fromMs != null) params.from = fromMs
  if (toMs   != null) params.to   = toMs
  const res = await axios.base().get(`${CHART_API}/history`, { params })
  // Server returns [{t, o, h, l, c, v}]; convert to Lightweight Charts shape (seconds).
  const raw = res.data.map(toLwc)
  return fillSmallGaps(raw, interval)
}

/**
 * Per-interval threshold for synthetic gap fill (seconds). Gaps larger than
 * the threshold (weekend FX close, overnight stock gaps, etc.) stay visible.
 * 0 = never fill.
 */
const GAP_FILL_MAX_SECONDS = {
  '1m':  4 * 60 * 60,
  '5m':  6 * 60 * 60,      // up to 6h
  '15m': 12 * 60 * 60,
  '30m': 12 * 60 * 60,
  '1h':  12 * 60 * 60,
  '4h':  0,
  '1d':  0,
  '1w':  0,
  '1M':  0,
}

function fillSmallGaps(bars, interval) {
  const intervalSec = INTERVAL_SECONDS[interval]
  const maxGapSec   = GAP_FILL_MAX_SECONDS[interval] || 0
  if (!intervalSec || !maxGapSec || bars.length < 2) return bars

  const out = [bars[0]]
  for (let i = 1; i < bars.length; i++) {
    const prev = out[out.length - 1]
    const curr = bars[i]
    const gap = curr.time - prev.time
    if (gap > intervalSec && gap <= maxGapSec) {
      const c = prev.close
      for (let t = prev.time + intervalSec; t < curr.time; t += intervalSec) {
        out.push({ time: t, open: c, high: c, low: c, close: c })
      }
    }
    out.push(curr)
  }
  return out
}

function toLwc(b) {
  return {
    time:  Math.floor(b.t / 1000),
    open:  b.o,
    high:  b.h,
    low:   b.l,
    close: b.c,
  }
}

/** Build the WebSocket URL, falling back to same-origin when no override is set. */
function buildWsUrl() {
  if (CHART_WS) return `${CHART_WS}`
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
  return `${scheme}://${window.location.host}/api/chart/ws`
}

/**
 * Open a chart WebSocket and subscribe to a symbol. Aggregates incoming 1m
 * candles into the caller's selected interval.
 *
 * @param {Object} opts
 * @param {string} opts.symbol
 * @param {string} opts.interval     - one of INTERVAL_SECONDS keys
 * @param {function(bar)} opts.onBar - called with the projected bar for the current interval
 * @returns {{ close: () => void, setInterval: (newInterval: string) => void }}
 */
export function subscribeLive({ symbol, interval, onBar }) {
  const state = {
    symbol,
    interval,
    intervalSec: INTERVAL_SECONDS[interval],
    currentBar: null,  // last projected bar at the chosen interval
    ws: null,
    closed: false,
    backoffMs: 1000,
  }

  function project(candle1mSec) {
    const sec = state.intervalSec
    const bucket = Math.floor(candle1mSec.time / sec) * sec
    if (state.currentBar == null || bucket > state.currentBar.time) {
      state.currentBar = {
        time:  bucket,
        open:  candle1mSec.open,
        high:  candle1mSec.high,
        low:   candle1mSec.low,
        close: candle1mSec.close,
      }
    } else if (bucket === state.currentBar.time) {
      state.currentBar.high  = Math.max(state.currentBar.high, candle1mSec.high)
      state.currentBar.low   = Math.min(state.currentBar.low,  candle1mSec.low)
      state.currentBar.close = candle1mSec.close
    } else {
      // Out-of-order 1m frame older than our running bar — ignore.
      return null
    }
    return state.currentBar
  }

  function open() {
    if (state.closed) return
    const ws = new WebSocket(buildWsUrl())
    state.ws = ws

    ws.onopen = () => {
      state.backoffMs = 1000
      ws.send(JSON.stringify({ action: 'subscribe', symbol: state.symbol }))
    }

    ws.onmessage = (event) => {
      if (state.closed) return
      let msg
      try { msg = JSON.parse(event.data) } catch { return }
      if (msg.type !== 'candle') return
      if (msg.symbol !== state.symbol) {
        // Loud during development so symbol-naming drift doesn't fail silently.
        // Remove once picker + storage names are aligned.
        if (typeof console !== 'undefined') {
          console.debug('[chart] skipped candle: subscribed=%o, incoming=%o',
            state.symbol, msg.symbol)
        }
        return
      }
      const sec = {
        time:  Math.floor(msg.t / 1000),
        open:  msg.o,
        high:  msg.h,
        low:   msg.l,
        close: msg.c,
      }

      // Fill any small gap between the previous bar and this new bucket.
      const intervalSec = state.intervalSec
      const newBucket = Math.floor(sec.time / intervalSec) * intervalSec
      if (state.currentBar != null && newBucket > state.currentBar.time + intervalSec) {
        const maxGap = GAP_FILL_MAX_SECONDS[state.interval] || 0
        const gap = newBucket - state.currentBar.time
        if (gap <= maxGap) {
          const c = state.currentBar.close
          for (let t = state.currentBar.time + intervalSec; t < newBucket; t += intervalSec) {
            onBar({ time: t, open: c, high: c, low: c, close: c }, true)
          }
        }
      }

      const bar = project(sec)
      if (bar) onBar({ ...bar }, msg.final === true)
    }

    ws.onclose = () => {
      if (state.closed) return
      setTimeout(open, state.backoffMs)
      state.backoffMs = Math.min(state.backoffMs * 2, 30_000)
    }

    ws.onerror = () => { /* close handler will reconnect */ }
  }

  open()

  return {
    close() {
      state.closed = true
      try {
        if (state.ws && state.ws.readyState === WebSocket.OPEN) {
          state.ws.send(JSON.stringify({ action: 'unsubscribe', symbol: state.symbol }))
        }
        if (state.ws) state.ws.close()
      } catch (_) { /* noop */ }
    },
    setInterval(newInterval) {
      state.interval = newInterval
      state.intervalSec = INTERVAL_SECONDS[newInterval]
      state.currentBar = null
    },
    setSymbol(newSymbol) {
      if (state.ws && state.ws.readyState === WebSocket.OPEN) {
        state.ws.send(JSON.stringify({ action: 'unsubscribe', symbol: state.symbol }))
        state.symbol = newSymbol
        state.currentBar = null
        state.ws.send(JSON.stringify({ action: 'subscribe', symbol: newSymbol }))
      } else {
        state.symbol = newSymbol
        state.currentBar = null
      }
    },
  }
}
